/**
 * @module Skeleton
 *
 * Loading placeholder with pulse animation. Use to indicate content is loading.
 *
 * @example
 * // Single skeleton
 * <Skeleton className="h-4 w-48" />
 *
 * @example
 * // Multiple skeletons
 * <Skeleton className="h-4 w-full" items={3} />
 *
 * @example
 * // Complex loading state with SkeletonFragment
 * <SkeletonFragment items={3}>
 *   <div className="flex gap-2">
 *     <Skeleton className="h-12 w-12 rounded-full" />
 *     <div className="space-y-2">
 *       <Skeleton className="h-4 w-32" />
 *       <Skeleton className="h-4 w-24" />
 *     </div>
 *   </div>
 * </SkeletonFragment>
 */
import type { ReactNode } from "react";
import { Fragment } from "react";
import { cn } from "../lib/utils";

/**
 * Animated loading placeholder.
 * @param items - Number of skeleton elements to render (default: 1)
 */
const Skeleton = ({
  className,
  items = 1,
  ref,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  items?: number;
  ref?: React.Ref<HTMLDivElement>;
}) => Array.from({
  length: items
}).map((_, i) => <div className={cn("animate-pulse rounded-md bg-gray-100 dark:bg-gray-900", className)} key={i} ref={ref} {...props} />);

/**
 * Repeats children multiple times for complex loading layouts.
 * @param items - Number of times to repeat children (default: 1)
 */
const SkeletonFragment = ({
  items = 1,
  children
}: {
  items?: number;
  children?: ReactNode;
}) => Array.from({
  length: items
}).map((_, i) => <Fragment key={i}>{children}</Fragment>);
export { Skeleton, SkeletonFragment };